import { HttpAgent } from '@icp-sdk/core/agent'
import { getAddress } from 'viem'
import { SOL_ID } from '../../constants/tokenIds'
import { Value } from '../../idls/tokens/icrc1.did'
import { Chain, EVMChain, PlugToken } from '../../interfaces/common'
import getTokenActor from '../../utils/tokenActions/getTokenActor'
import { getERC20TokenMetadata } from '../eth'
import { SOLANA_BASE_FEE } from '../solana'

export interface UnknownToken {
  evmChain?: EVMChain
  chain: string
  balance: bigint
  address: string
}

interface SPLToken {
  id: string
  name: string
  symbol: string
  balance: string
  decimals: number
  icon: string
  tokenProgram: string
}

const getERC20TokenLogo = (chainName: string, address: string) => {
  return `https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/${chainName.toLowerCase()}/assets/${getAddress(address)}/logo.png`
}

const parseMetadata = (metadata: [string, Value][]) => {
  const normalizedMeta = Object.fromEntries(metadata.map(([k, v]) => [k.replace('icrc1:', ''), v]))
  const result: Pick<PlugToken, 'fee' | 'name' | 'symbol' | 'decimals' | 'logo'> = {
    fee: BigInt(0),
    name: '',
    symbol: '',
    decimals: 8,
    logo: '',
  }

  if (normalizedMeta.fee && 'Nat' in normalizedMeta.fee) {
    result.fee = normalizedMeta.fee.Nat
  }

  if (normalizedMeta.name && 'Text' in normalizedMeta.name) {
    result.name = normalizedMeta.name.Text
  }

  if (normalizedMeta.symbol && 'Text' in normalizedMeta.symbol) {
    result.symbol = normalizedMeta.symbol.Text
  }

  if (normalizedMeta.decimals && 'Nat' in normalizedMeta.decimals) {
    result.decimals = Number(normalizedMeta.decimals.Nat)
  }

  if (normalizedMeta.logo && 'Text' in normalizedMeta.logo) {
    result.logo = normalizedMeta.logo.Text
  }

  return result
}

const getICPTokenMetadata = async (canisterId: string, standard: string, agent: HttpAgent) => {
  const actor = getTokenActor(canisterId, standard, agent)

  const icrcActor = actor.icp || actor.icrc1 || actor.icrc2
  if (icrcActor) {
    const metadata = await icrcActor.icrc1_metadata()
    return {
      ...parseMetadata(metadata),
    }
  }

  if (actor.dip20) {
    const {
      metadata: { fee, name, logo, symbol, decimals },
    } = await actor.dip20.getTokenInfo()
    return {
      name,
      symbol,
      decimals,
      fee,
      logo,
    }
  }

  if (actor.drc20) {
    const name = await actor.drc20.drc20_name()
    const symbol = await actor.drc20.drc20_symbol()
    const decimals = await actor.drc20.drc20_decimals()
    const fee = await actor.drc20.drc20_fee()
    const metadata = await actor.drc20.drc20_metadata()
    const logo = metadata.find((md) => md.name === 'logo')?.content || ''
    return {
      name,
      symbol,
      decimals,
      fee,
      logo,
    }
  }

  if (actor.xtc) {
    const meta = await actor.xtc.getMetadata()
    return {
      name: meta.name,
      symbol: meta.symbol,
      decimals: meta.decimals,
      fee: meta.fee,
      logo: meta.logo,
    }
  }

  return Promise.reject('No token actor')
}

const getSplTokenMetadata = async (mintAddress: string): Promise<SPLToken | undefined> => {
  const searchResponse = await (
    await fetch(`https://lite-api.jup.ag/tokens/v2/search?query=${mintAddress}`)
  ).json()
  return searchResponse[0]
}

const getTokenMetadata = async (
  contractAddress: string,
  chain: string,
  options?: {
    standard?: string
    agent?: HttpAgent
    evmChain?: EVMChain
  }
): Promise<PlugToken | undefined> => {
  if (chain === Chain.ICP && options?.agent) {
    if (!options.standard) {
      throw Error("Can't fetch metadata for ICP token without standard")
    }

    const icpToken = await getICPTokenMetadata(contractAddress, options.standard, options.agent)
    return {
      id: `icp-${contractAddress}`,
      chain: Chain.ICP,
      name: icpToken.name,
      address: contractAddress,
      standard: 'XTC',
      symbol: icpToken.symbol,
      decimals: icpToken.decimals,
      fee: icpToken.fee,
      logo: icpToken.logo,
      tags: [],
      analyticsDetails: {},
      additionalData: {},
      isCustom: true,
    }
  } else if (chain === Chain.SOL) {
    const splToken = await getSplTokenMetadata(contractAddress)
    if (splToken) {
      return {
        id: `sol-${contractAddress}`,
        name: splToken.name,
        symbol: splToken.symbol,
        decimals: splToken.decimals,
        standard: 'SPL',
        fee_token_id: SOL_ID,
        fee: SOLANA_BASE_FEE,
        logo: splToken.icon,
        address: contractAddress,
        chain: Chain.SOL,
        tags: [],
        analyticsDetails: {},
        additionalData: {
          solana_program_id: splToken.tokenProgram,
        },
        isCustom: true,
      }
    }
  } else if (options?.evmChain) {
    const erc20 = await getERC20TokenMetadata(options.evmChain, contractAddress as `0x${string}`)
    return {
      id: `${options.evmChain.chainId}-${contractAddress}`,
      name: erc20.name,
      symbol: erc20.symbol,
      decimals: erc20.decimals,
      standard: 'ERC20',
      fee_token_id: options.evmChain.feeTokenId,
      fee: 0n,
      logo: getERC20TokenLogo(options.evmChain.name, contractAddress),
      address: contractAddress,
      chain: options.evmChain.chainId.toString() as Chain,
      evmChain: options.evmChain,
      tags: [],
      analyticsDetails: {},
      additionalData: {},
      isCustom: true,
    }
  } else {
    throw new Error(`Unsupported chain: ${chain}`)
  }
}

export default getTokenMetadata
